import { useEffect, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useFrameTypes, useSizes, useCommissions, useUpdateCommission } from '../hooks/useCommissions'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { ArtworkStatus, type Commission } from '@/types/entities'
import { customerPrice, effectiveCommission, gbp, MAX_COMMISSION } from '../utils/commission'

interface CommissionsViewProps {
  artworkId: number
  status?: ArtworkStatus
  readOnly?: boolean
}

export function CommissionsView({ artworkId, status, readOnly = false }: CommissionsViewProps) {
  const [selectedFrameType, setSelectedFrameType] = useState(1)
  const { data: frameTypes } = useFrameTypes()
  const { data: sizes } = useSizes()
  const { data: commissions, isLoading } = useCommissions(artworkId)
  const { mutate: updateCommission, isPending } = useUpdateCommission()

  // A live artwork's prices and commissions are fixed on its listing when it is published, so a
  // change here would not reach the shop. Say so rather than accept an edit that does nothing.
  const live = status === ArtworkStatus.PUBLISHED
  const editable = !readOnly && !live

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const rows = (sizes ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((size) => ({
      size,
      commission: commissions?.find(
        (c) => c.frameTypeId === selectedFrameType && c.sizeId === size.id
      ),
    }))
    .filter((r) => r.commission)

  const save = (c: Commission, data: { commission: number; isCustom: boolean }) =>
    updateCommission({
      artworkId,
      data: { sizeId: c.sizeId, frameTypeId: c.frameTypeId, ...data },
    })

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium">Product Type:</label>
        <Select
          value={selectedFrameType.toString()}
          onValueChange={(v) => setSelectedFrameType(Number(v))}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {frameTypes?.map((ft) => (
              <SelectItem key={ft.id} value={ft.id.toString()}>
                {ft.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Size</TableHead>
              <TableHead>Base price</TableHead>
              <TableHead>You earn per sale</TableHead>
              <TableHead>Customer pays</TableHead>
              <TableHead>Custom</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  No sizes for this product type.
                </TableCell>
              </TableRow>
            )}
            {rows.map(({ size, commission }) => {
              const c = commission as Commission
              const price = customerPrice(c)
              return (
                <TableRow key={size.id}>
                  <TableCell className="font-medium">{size.name}</TableCell>
                  <TableCell>{c.basePrice == null ? '—' : gbp.format(Number(c.basePrice))}</TableCell>
                  <TableCell>
                    {editable && c.isCustom ? (
                      <CommissionInput
                        value={Number(c.commission) || 0}
                        disabled={isPending}
                        onCommit={(value) => save(c, { commission: value, isCustom: true })}
                      />
                    ) : (
                      <span>
                        {gbp.format(effectiveCommission(c))}
                        {!c.isCustom && (
                          <span className="ml-1 text-xs text-muted-foreground">(default)</span>
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-semibold">
                    {price == null ? '—' : gbp.format(price)}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Switch
                        checked={c.isCustom}
                        disabled={isPending}
                        aria-label={`Set my own commission for ${size.name}`}
                        onCheckedChange={(isCustom) =>
                          save(c, {
                            // Start a custom figure from what they earn now.
                            commission: isCustom ? effectiveCommission(c) : Number(c.commission) || 0,
                            isCustom,
                          })
                        }
                      />
                    ) : (
                      <span>{c.isCustom ? 'Yes' : 'No'}</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-muted-foreground">
        {live ? (
          <>
            <strong>This artwork is live.</strong> Its prices were set when it was published. To
            change what you earn on it, write to support@myarthaus.com.
          </>
        ) : editable ? (
          <>
            Your commission is the amount you earn on each sale, added to the base price. Turn
            on <strong>Custom</strong> to set your own for a size; leave it off to earn the default.
          </>
        ) : (
          <>Click Edit to change what you earn on each size.</>
        )}
      </p>
    </div>
  )
}

/** A pounds field that saves when it loses focus or on Enter, not on every keystroke. */
function CommissionInput({
  value,
  disabled,
  onCommit,
}: {
  value: number
  disabled?: boolean
  onCommit: (value: number) => void
}) {
  const [text, setText] = useState(value.toFixed(2))
  useEffect(() => setText(value.toFixed(2)), [value])

  const commit = () => {
    const n = Math.round(Number(text) * 100) / 100
    if (!Number.isFinite(n) || n < 0 || n > MAX_COMMISSION) {
      setText(value.toFixed(2))
      return
    }
    if (n !== value) onCommit(n)
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm text-muted-foreground">£</span>
      <Input
        type="number"
        inputMode="decimal"
        step="0.50"
        min="0"
        max={MAX_COMMISSION}
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
        className="w-24"
      />
    </div>
  )
}
